import { StoreRole } from '@app/shared';
import { Flex, Spin } from 'antd';
import { Navigate, Outlet } from 'react-router-dom';
import { useListMembershipsQuery } from '@/entities/user';
import { useAppSelector } from '@/app/store/hooks';
import { ROUTES } from '@/shared/config/routes';
import { QueryErrorAlert } from '@/shared/ui';

export interface RequireMembershipProps {
  /** Restricts to these StoreRole values; omit to allow any approved role. */
  roles?: readonly string[];
}

/** Gate for `/admin` and `/shop` — checks the current store's membership status/role, not just
 *  that the user is logged in (see `RequireAuth` for that).
 *  - no memberships at all → `/onboarding` (create a store or join one);
 *  - memberships exist but none is selected for the current store → `/` (`RootRedirect` picks
 *    the right zone/store);
 *  - a non-approved CLIENT row → `/connections` (status of the request), any other non-approved
 *    row → `/no-access`;
 *  - an approved membership with a role outside `roles` → `/no-access`. */
export function RequireMembership({ roles }: RequireMembershipProps) {
  const currentStoreId = useAppSelector((state) => state.store.currentStoreId);
  const { data: memberships, error, isLoading, refetch } = useListMembershipsQuery();

  // A failed fetch is not «no memberships» — say so and let the user retry instead of spinning forever.
  if (!memberships && error) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh', padding: 16 }}>
        <QueryErrorAlert error={error} onRetry={() => void refetch()} />
      </Flex>
    );
  }

  if (isLoading || !memberships) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100vh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (memberships.length === 0) {
    return <Navigate to={ROUTES.onboarding} replace />;
  }

  const membership = currentStoreId
    ? memberships.find((m) => m.storeId === currentStoreId)
    : undefined;

  if (!membership) {
    return <Navigate to="/" replace />;
  }
  if (membership.status !== 'APPROVED') {
    return (
      <Navigate
        to={membership.role === StoreRole.CLIENT ? ROUTES.connections : ROUTES.noAccess}
        replace
      />
    );
  }
  if (roles && !roles.includes(membership.role)) {
    return <Navigate to={ROUTES.noAccess} replace />;
  }

  return <Outlet />;
}
